import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import { ApiError, setUnverifiedHandler } from "@/api/fetchApi";
import { AuthProvider } from "@/auth/AuthProvider";
import { AppDrawerProvider } from "@/components/AppDrawer";
import { ToastProvider } from "@/components/Toast";

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 4xx answers are final; retry network and server errors twice.
        retry: (count, error) =>
          !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

/** Everything every route needs: server state, auth, toasts and the dev drawer slot. */
export function Providers({ children, client }: { children: ReactNode; client?: QueryClient }) {
  const [queryClient] = useState(() => client ?? makeQueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <AppDrawerProvider>{children}</AppDrawerProvider>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export function RootLayout() {
  const navigate = useNavigate();
  useEffect(() => {
    // A 403 EMAIL_NOT_VERIFIED from any call sends the user to the verify-email gate.
    setUnverifiedHandler(() => {
      if (window.location.pathname !== "/verify-email") navigate("/verify-email");
    });
  }, [navigate]);
  return (
    <Providers>
      <Outlet />
    </Providers>
  );
}
